// src/odoo/transactions_dashboard/api/TransactionsDashboardApi.ts
// All comments are in English.

import { http } from "@/core/http";

// ----------------------------------------------------------------------------
// Shared types for transactions dashboard
// ----------------------------------------------------------------------------

export interface ConfirmedOrdersTrendPoint {
  month_start: string; // ISO date string (YYYY-MM-01)
  confirmed_amount: string;
  quotation_amount: string;
}

export interface ConfirmedVsInvoicedTrendPoint {
  day: string; // ISO date string (YYYY-MM-DD)
  confirmed_amount: string;
  invoiced_amount: string;
}

export interface TopCustomerItem {
  partner_id: number;
  partner_name: string;
  total_billed: string;
  pending_to_pay?: string | number | null;
  currency_name?: string | null;
}

export interface ProductRankingItem {
  product_model: string;
  total_quantity: string;
  total_revenue: string;
}

export interface ProductRankingsResponse {
  by_quantity: ProductRankingItem[];
  by_revenue: ProductRankingItem[];
}

export interface IntraBrandShareItem {
  product_model: string;
  total_quantity: string;
  total_revenue: string;
  contribution_pct: string;
}

export interface IntraBrandShareBrandGroup {
  brand_grouping: string;
  brand_total_revenue: string;
  items: IntraBrandShareItem[];
}

export interface IntraBrandShareResponse {
  brands: IntraBrandShareBrandGroup[];
}

export interface MonthlyProfitPoint {
  month_start: string; // ISO date string (YYYY-MM-DD)
  revenue: string;
  cost: string;
  gross_profit: string;
}

export interface TransactionsDashboardFilters {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  companyId?: number;
  activeOnly?: boolean;
  limit?: number;
}

export interface UninvoicedTrendPoint {
  month_start: string; // ISO date, e.g. "2025-01-01"
  confirmed_amount: number;
  invoiced_amount: number;
  uninvoiced_amount: number;
}

// -----------------------------------------------------------------------------
// Month-level detail for open quotations (and legacy confirmed-orders schema)
// -----------------------------------------------------------------------------

export interface ConfirmedOrderItem {
  order_id: number;
  order_name: string;

  partner_id?: number | null;
  partner_name?: string | null;

  salesperson_id?: number | null;
  salesperson_name?: string | null;

  team_id?: number | null;
  team_name?: string | null;

  company_id?: number | null;
  company_name?: string | null;

  currency_name?: string | null;

  created_on: string;          // "2024-10-14"
  confirmed_on: string | null; // "2025-01-02" or null

  amount_total: string;        // "27516.000000"
}

export interface QuotationOrderItem {
  order_id: number;
  order_name: string;

  partner_id?: number | null;
  partner_name?: string | null;

  salesperson_id?: number | null;
  salesperson_name?: string | null;

  team_id?: number | null;
  team_name?: string | null;

  company_id?: number | null;
  company_name?: string | null;

  currency_name?: string | null;

  // For overdue quotations: due date and overdue days
  due_date?: string | null;        // "YYYY-MM-DD"
  overdue_days?: number | null;    // e.g. 15

  // Legacy fields (still kept for compatibility)
  created_on?: string | null;
  confirmed_on?: string | null;
  amount_total: string;
}


export interface ConfirmedOrdersMonthDetail {
  // For historical reasons the type name and field names still reference
  // "confirmed orders", but the backend currently only returns open quotations
  // (confirmed_orders is always an empty array).
  month_start: string; // ISO date string (YYYY-MM-01)
  confirmed_orders: ConfirmedOrderItem[];
  quotations: QuotationOrderItem[];
}

export interface UninvoicedMonthDetail {
  month_start: string; // ISO date string (YYYY-MM-01)
  orders: ConfirmedOrderItem[];
}

// -----------------------------------------------------------------------------
// Invoice payment status + overdue invoices types
// -----------------------------------------------------------------------------

export interface InvoicePaymentStatusTrendPoint {
  month_start: string;    // "YYYY-MM-01"
  billed_amount: string;  // total billed (due in this month)
  overdue_amount: string; // portion that is already overdue
}

// Overdue invoice month-detail types
// Overdue invoice month-detail types
export interface OverdueInvoiceItem {
  invoice_id: number;
  invoice_name: string;
  partner_name: string | null;
  invoice_date: string | null;
  invoice_date_due: string | null;
  amount_total: string | number | null;
  payment_state: string | null;

  // New: outstanding balance (from fi.amount_residual)
  amount_residual?: string | number | null;
}

export interface OverdueInvoiceMonthDetail {
  month_start: string;
  invoices: OverdueInvoiceItem[];
}

// -----------------------------------------------------------------------------
// API calls (axios via http.ts)
// -----------------------------------------------------------------------------

export async function fetchConfirmedOrdersTrend(
  filters: TransactionsDashboardFilters
): Promise<ConfirmedOrdersTrendPoint[]> {
  const res = await http.get<ConfirmedOrdersTrendPoint[]>(
    "/transactions-dashboard/confirmed-orders-trend",
    {
      params: {
        start_date: filters.startDate,
        end_date: filters.endDate,
        company_id: filters.companyId,
      },
    }
  );
  return res.data;
}

export async function fetchConfirmedVsInvoicedTrend(
  filters: TransactionsDashboardFilters
): Promise<ConfirmedVsInvoicedTrendPoint[]> {
  const res = await http.get<ConfirmedVsInvoicedTrendPoint[]>(
    "/transactions-dashboard/confirmed-vs-invoiced-trend",
    {
      params: {
        start_date: filters.startDate,
        end_date: filters.endDate,
        company_id: filters.companyId,
      },
    }
  );
  return res.data;
}

export async function fetchTopCustomers(
  filters: TransactionsDashboardFilters
): Promise<TopCustomerItem[]> {
  const res = await http.get<TopCustomerItem[]>(
    "/transactions-dashboard/top-customers",
    {
      params: {
        start_date: filters.startDate,
        end_date: filters.endDate,
        company_id: filters.companyId,
        active_only: filters.activeOnly ?? false,
        limit: filters.limit ?? 10,
      },
    }
  );
  return res.data;
}

export async function fetchProductRankings(
  filters: TransactionsDashboardFilters
): Promise<ProductRankingsResponse> {
  const res = await http.get(
    "/transactions-dashboard/product-rankings",
    {
      params: {
        start_date: filters.startDate,
        end_date: filters.endDate,
        company_id: filters.companyId,
        active_only: filters.activeOnly ?? false,
        limit: filters.limit ?? 10,
      },
    }
  );

  const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

  const asNumberString = (value: unknown): string => {
    if (typeof value === "number") return String(value);
    if (typeof value === "string") return value;
    return "0";
  };

  const pickString = (
    row: Record<string, unknown>,
    keys: string[],
    fallback: string
  ): string => {
    for (const key of keys) {
      const v = row[key];
      if (typeof v === "string" && v.trim() !== "") return v.trim();
    }
    return fallback;
  };

  const normalizeRow = (raw: unknown): ProductRankingItem => {
    const row = asRecord(raw);
    return {
      product_model: pickString(
        row,
        ["product_model", "productModel", "product_name", "productName"],
        "Unspecified product"
      ),
      total_quantity: asNumberString(
        row.total_quantity ?? row.totalQuantity ?? row.quantity ?? row.sum_qty
      ),
      total_revenue: asNumberString(
        row.total_revenue ??
          row.totalRevenue ??
          row.revenue ??
          row.sum_pip_total ??
          row.pip_total
      ),
    };
  };

  const payload = asRecord(res.data);
  const byQuantityRaw = Array.isArray(payload.by_quantity)
    ? payload.by_quantity
    : [];
  const byRevenueRaw = Array.isArray(payload.by_revenue) ? payload.by_revenue : [];

  return {
    by_quantity: byQuantityRaw.map(normalizeRow),
    by_revenue: byRevenueRaw.map(normalizeRow),
  };
}

export async function fetchMonthlyProfit(
  filters: TransactionsDashboardFilters
): Promise<MonthlyProfitPoint[]> {
  const res = await http.get<MonthlyProfitPoint[]>(
    "/transactions-dashboard/monthly-profit",
    {
      params: {
        start_date: filters.startDate,
        end_date: filters.endDate,
        company_id: filters.companyId,
      },
    }
  );
  return res.data;
}

export async function fetchIntraBrandShare(
  filters: TransactionsDashboardFilters
): Promise<IntraBrandShareResponse> {
  const res = await http.get("/transactions-dashboard/intra-brand-share", {
    params: {
      start_date: filters.startDate,
      end_date: filters.endDate,
      company_id: filters.companyId,
      active_only: filters.activeOnly ?? false,
      limit_per_brand: filters.limit ?? 10,
    },
  });

  const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

  const asNumberString = (value: unknown): string => {
    if (typeof value === "number") return String(value);
    if (typeof value === "string") return value;
    return "0";
  };

  const pickString = (
    row: Record<string, unknown>,
    keys: string[],
    fallback: string
  ): string => {
    for (const key of keys) {
      const v = row[key];
      if (typeof v === "string" && v.trim() !== "") return v.trim();
    }
    return fallback;
  };

  const normalizeItem = (raw: unknown): IntraBrandShareItem => {
    const row = asRecord(raw);
    return {
      product_model: pickString(
        row,
        ["product_model", "productModel", "product_name", "productName"],
        "Unspecified product"
      ),
      total_quantity: asNumberString(
        row.total_quantity ?? row.totalQuantity ?? row.quantity ?? row.sum_qty
      ),
      total_revenue: asNumberString(
        row.total_revenue ?? row.totalRevenue ?? row.revenue ?? row.sum_pip_total
      ),
      contribution_pct: asNumberString(
        row.contribution_pct ?? row.contributionPct ?? row.share_pct
      ),
    };
  };

  const normalizeBrand = (raw: unknown): IntraBrandShareBrandGroup => {
    const row = asRecord(raw);
    const items = Array.isArray(row.items) ? row.items : [];
    const brandGrouping = pickString(
      row,
      ["brand_grouping", "brandGrouping", "brand"],
      "Others"
    );
    return {
      brand_grouping:
        brandGrouping.trim().toLowerCase() === "chaos" ? "Chaosgroup" : brandGrouping,
      brand_total_revenue: asNumberString(
        row.brand_total_revenue ?? row.brandTotalRevenue ?? row.total_revenue
      ),
      items: items.map(normalizeItem),
    };
  };

  const payload = asRecord(res.data);
  const brandsRaw = Array.isArray(payload.brands) ? payload.brands : [];
  return { brands: brandsRaw.map(normalizeBrand) };
}

export async function fetchUninvoicedTrend(
  filters: TransactionsDashboardFilters
): Promise<UninvoicedTrendPoint[]> {
  const res = await http.get<UninvoicedTrendPoint[]>(
    "/transactions-dashboard/uninvoiced-trend",
    {
      params: {
        start_date: filters.startDate,
        end_date: filters.endDate,
        company_id: filters.companyId,
      },
    }
  );
  return res.data;
}

// -----------------------------------------------------------------------------
// API: open quotations month detail
// -----------------------------------------------------------------------------

export async function fetchOpenQuotationsMonthDetail(params: {
  monthStart: string;
  companyId?: number;
}): Promise<ConfirmedOrdersMonthDetail> {
  const res = await http.get<ConfirmedOrdersMonthDetail>(
    "/transactions-dashboard/open-quotations-month-detail",
    {
      params: {
        month_start: params.monthStart,
        company_id: params.companyId,
      },
    }
  );
  return res.data;
}

export async function fetchUninvoicedMonthDetail(params: {
  monthStart: string;
  companyId?: number;
}): Promise<UninvoicedMonthDetail> {
  const res = await http.get<UninvoicedMonthDetail>(
    "/transactions-dashboard/uninvoiced-month-detail",
    {
      params: {
        month_start: params.monthStart,
        company_id: params.companyId,
      },
    }
  );
  return res.data;
}

export async function fetchInvoicePaymentStatusTrend(
  filters: TransactionsDashboardFilters
): Promise<InvoicePaymentStatusTrendPoint[]> {
  const res = await http.get<InvoicePaymentStatusTrendPoint[]>(
    "/transactions-dashboard/invoice-payment-status-trend",
    {
      params: {
        start_date: filters.startDate,
        end_date: filters.endDate,
        company_id: filters.companyId,
      },
    }
  );
  return res.data;
}

export async function fetchOverdueInvoicesMonthDetail(params: {
  monthStart: string;
  companyId?: number;
}): Promise<OverdueInvoiceMonthDetail> {
  const res = await http.get<OverdueInvoiceMonthDetail>(
    "/transactions-dashboard/overdue-invoices-month-detail",
    {
      params: {
        month_start: params.monthStart,
        company_id: params.companyId,
      },
    }
  );
  return res.data;
}
